import { NavigationGuard, Route, NavigationGuardNext } from 'vue-router';
import { DataResolver, resolvers } from './data-resolvers';
import { AppRoute } from '@/types/route';
import Vue from 'vue';


// Mapa de resolvers asociados a cada ruta por nombre
const routeResolversMap = new Map<string, DataResolver>();

// Event bus para comunicar el estado de carga de los resolvers
export const resolverEvents = new Vue();

// Estado de carga de resolvers por ruta
export const resolverLoadingState = {
  isLoading: false,
  currentRoute: ''
};

/**
 * Registra un resolver de datos para una ruta específica
 * @param routeName Nombre de la ruta
 * @param resolver Función resolver que precarga datos
 */
export function registerRouteResolver(routeName: string, resolver: DataResolver): void {
  routeResolversMap.set(routeName, resolver);
}

/**
 * Obtiene el resolver asociado a una ruta
 * @param routeName Nombre de la ruta
 */
export function getRouteResolver(routeName: string): DataResolver | undefined {
  return routeResolversMap.get(routeName);
}

/**
 * Verifica si una ruta tiene un resolver asociado
 * @param routeName Nombre de la ruta
 */
export function hasRouteResolver(routeName: string): boolean {
  return routeResolversMap.has(routeName);
}

/**
 * Crea una guardia de navegación que ejecuta el resolver de datos
 * antes de completar la navegación
 */
// Global flag to track resolver execution to prevent duplicates
const executedResolvers = new Map<string, number>();
// Flag to track the current navigation cycle
let currentNavigationId = 0;

export const dataResolverGuard: NavigationGuard = async (
  to: Route,
  from: Route,
  next: NavigationGuardNext
): Promise<void> => {
  try {
    const routeName = to.name || '';
    const resolver = getRouteResolver(routeName);
    
    // Always call next() early so navigation isn't blocked
    next();
    
    // Generate a unique execution ID for this navigation
    currentNavigationId = Date.now();
    const navigationId = currentNavigationId;
    
    if (resolver) {
      // Check if this resolver has already been executed for this navigation
      const lastExecution = executedResolvers.get(routeName);
      const currentTime = Date.now();
      
      if (lastExecution && (currentTime - lastExecution < 1000)) {
        console.log(`Skipping duplicate resolver execution for route '${routeName}'`);
        return;
      }
      
      // Mark this resolver as executed
      executedResolvers.set(routeName, currentTime);
      
      console.log(`Executing resolver for route '${routeName}'`);
      
      // Notificar inicio de carga
      resolverLoadingState.isLoading = true;
      resolverLoadingState.currentRoute = routeName;
      resolverEvents.$emit('resolver:start', { routeName });
      
      try {
        await resolver(to, from);
        console.log(`Resolver execution completed for route '${routeName}'`);
      } catch (resolverError) {
        console.error(`Error executing resolver for route '${routeName}':`, resolverError);
      }
      
      // Only update loading state if this is still the current navigation
      if (navigationId === currentNavigationId) {
        // Notificar fin de carga
        resolverLoadingState.isLoading = false;
        console.log(`Emitting resolver:end event for route '${routeName}'`);
        resolverEvents.$emit('resolver:end', { routeName });
      }
    }
  } catch (error) {
    console.error(`Error in data resolver for route '${to.name}':`, error);
    
    // Notificar error en carga
    resolverLoadingState.isLoading = false;
    resolverEvents.$emit('resolver:error', { routeName: to.name, error });
  }
};

/**
 * Combina una guardia de navegación existente con el resolver de datos
 * @param existingGuard Guardia de navegación existente
 */
export function withDataResolver(
  existingGuard?: NavigationGuard
): NavigationGuard {
  if (!existingGuard) {
    return dataResolverGuard;
  }

  return async (to: Route, from: Route, next: NavigationGuardNext): Promise<void> => {
    const routeName = to.name || '';
    const resolver = getRouteResolver(routeName);
    
    // Solo ejecutar el resolver si la ruta actual está registrada
    if (resolver) {
      try {
        // Check if this resolver has already been executed for this navigation
        const lastExecution = executedResolvers.get(routeName);
        const currentTime = Date.now();
        
        if (lastExecution && (currentTime - lastExecution < 500)) {
          console.log(`Skipping duplicate resolver execution in guard for route '${routeName}'`);
        } else {
          console.log(`Executing resolver before guard for route '${routeName}'`);
          
          // Mark this resolver as executed
          executedResolvers.set(routeName, currentTime);
          
          // Notificar inicio de carga
          resolverLoadingState.isLoading = true;
          resolverLoadingState.currentRoute = routeName;
          resolverEvents.$emit('resolver:start', { routeName });
          
          await resolver(to, from);
          
          // Notificar fin de carga
          resolverLoadingState.isLoading = false;
          resolverEvents.$emit('resolver:end', { routeName });
        }
      } catch (error) {
        console.error(`Error in data resolver for route '${routeName}':`, error);
        
        // Notificar error en carga
        resolverLoadingState.isLoading = false;
        resolverEvents.$emit('resolver:error', { routeName, error });
      }
    }
    
    // Ejecutar la guardia existente
    existingGuard(to, from, next);
  };
}

/**
 * Inicializa los resolvers de datos para rutas comunes
 */
export function initializeCommonResolvers(): void {
  console.log('Resolvers for common routes registered');
}

/**
 * Aplica resolvers a rutas existentes
 * @param routes Rutas existentes
 */
export function applyResolversToRoutes(routes: AppRoute[]): AppRoute[] {
  return routes.map(route => {
    // Solo aplicar los resolvers a las rutas que tienen un resolver registrado
    if (route.name && routeResolversMap.has(route.name)) {
      return {
        ...route,
        beforeEnter: withDataResolver(route.beforeEnter)
      };
    }
    
    // Para rutas sin resolver, dejar como están
    return route;
  });
}

/**
 * Obtiene la lista de todas las rutas que tienen resolvers
 */
export function getRoutesWithResolvers(): string[] {
  return Array.from(routeResolversMap.keys());
}